import Logger from "logdna";
import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import "./App.sass";
import useWebSocket, { ReadyState } from "react-use-websocket";

import CopyLink from "./components/CopyLink";
import ValidationSummary from "./components/validationSummary";
import BetsTable from "./components/betsTable";
import CustomVideoPlayer from "./components/CustomVideoPlayer";
import Currency from "./components/currency";
import GameButton from "./components/GameButton";
import DogNumber from "./components/dogNumber";
import HorseNumber from "./components/horseNumber";
import Leaderboard from "./components/leaderboard";
import Layout from "./components/layout";
import NotInvited from "./components/NotInvited";
import FinalScores from "./components/finalScores";
import MeetTitle from "./components/MeetTitle";
import GetStartedComponent from "./components/GetStartedComponent";
const Joi = require("@hapi/joi");
const format = require("date-format");
const QRCode = require("qrcode.react");

const plans = require("./plans");

const RACE_STATUS = require("./raceStatus");

const betsSchema = Joi.array()
  .items(
    Joi.object({
      amount: Joi.number().positive().precision(2).label("amount").required(),
      eachWay: Joi.boolean(),
    })
  )
  .options({ stripUnknown: true });

const scrollToRef = (ref) => window.scrollTo(0, ref.current.offsetTop);

function formatDuration(seconds) {
  const d = new Date(seconds * 1000);
  if (d.getMinutes() > 0) {
    return format.asString("mm:ss.SSS", d);
  }
  return format.asString("ss.SSS", d);
}

const socketUrl = process.env.REACT_APP_SOCKET_URL;
const httpUrl = window.location.origin;
const siteUrl = process.env.REACT_APP_SITE_URL;

const logger = Logger.createLogger(process.env.REACT_APP_LOG_DNA_KEY, {
  hostname: "client-device",
  app: "client-side-app",
  index_meta: true,
});

const getGameIds = () => {
  let cookieValue = document.cookie.replace(
    /(?:(?:^|.*;\s*)GameIds\s*\=\s*([^;]*).*$)|^.*$/,
    "$1"
  );
  try {
    return JSON.parse(cookieValue);
  } catch (ex) {
    return JSON.parse(atob(cookieValue));
  }
};

function Race() {
  const [meetId, setMeetId] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [balance, setBalance] = useState();
  const [placedBets, setPlacedBets] = useState([]);
  const [position, setPosition] = useState(1);
  const [bets, setBets] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [meet, setMeet] = useState(null);
  const [activeRace, setActiveRace] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showVideoWarning, setShowVideoWarning] = useState(false);
  const [showSpHelp, setShowSpHelp] = useState(false);
  const [videoHeight, setVideoHeight] = useState("400px");
  const [globalError, setGlobalError] = useState(null);
  const [videoStarted, setVideoStarted] = useState(false);
  const [plan, setPlan] = useState(null);
  const [showSharingPanel, setShowSharingPanel] = useState(false);
  const [maxConnectionAttepmtsHit, setMaxConnectionAttepmtsHit] =
    useState(false);
  const [notInvited, setNotInvited] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState(null);
  const betBox = useRef(null);
  const topOfPage = useRef(null);
  const executeScrollToTop = () => scrollToRef(topOfPage);
  const executeScroll = () => scrollToRef(betBox);

  const blockingJsonMessage = (message) => {
    setLoading(true);
    sendJsonMessage(message);
  };

  function toggleSharingPanel(e) {
    e.preventDefault();
    setShowSharingPanel(!showSharingPanel);
  }

  async function startMeet() {
    blockingJsonMessage({
      event: "start-meet",
      meetId,
    });
  }

  async function moveToNextRace() {
    setVideoStarted(false);
    blockingJsonMessage({
      event: "next-race",
      meetId,
    });
  }

  async function finishRaceNight() {
    blockingJsonMessage({
      event: "meet-finished",
      meetId,
    });
  }

  function racePlayed() {
    setVideoStarted(true);
  }

  async function raceFinished() {
    if (!videoStarted && !showVideoWarning) {
      setShowVideoWarning(true);
    } else {
      setShowVideoWarning(false);
      setVideoStarted(false);
      setVideoUrl(null);
      blockingJsonMessage({
        event: "race-finished",
        meetId,
      });
    }
  }
  async function startNextRace() {
    blockingJsonMessage({
      event: "start-next-race",
      meetId,
    });
  }

  function showGlobalError(message) {
    setGlobalError(message);
    setTimeout(() => {
      setGlobalError(null);
    }, 3000);
  }

  function getFinalLeaderboard() {
    blockingJsonMessage({
      event: "get-final-leaderboard",
      meetId,
    });
  }

  function getBets(activeRaceId) {
    sendJsonMessage({
      event: "get-bets",
      meetId,
      participantId,
      raceId: activeRaceId,
    });
  }

  function handleResize() {
    const mainContainer = document.getElementById("mainContainer");
    setVideoHeight(`${mainContainer.clientWidth / 2}px`);
  }

  function dogOrHorse() {
    return raceTypeTitle().toLowerCase();
  }

  function raceTypeTitle() {
    return meet && meet.type === "horses" ? "Horse" : "Dog";
  }

  function horseyDistance(distanceInYards) {
    try {
      const [yards] = distanceInYards.split(" ");
      if (yards > 1760) {
        const miles = yards / 1760;
        const partMiles = miles % 1;
        const fullMiles = Math.floor(miles);
        if (partMiles > 0.1) {
          const furlongs = Math.floor(partMiles * 8);
          return `${fullMiles} mile, ${furlongs} ${
            furlongs > 1 ? "furlongs" : "furlong"
          }`;
        } else {
          return `${fullMiles} miles`;
        }
      }

      return `${Math.floor(yards * 0.00454545)} furlongs`;
    } catch (ex) {
      return distanceInYards;
    }
  }

  const getSocketUrl = useCallback(() => {
    try {
      const gameIds = getGameIds();
      if (!gameIds.MeetId || !gameIds.ParticipantId) {
        logger.log("NOT AUTHORISED: gameIds not found in cookies");
        setNotInvited(true);
      }
      setMeetId(gameIds.MeetId);
      setParticipantId(gameIds.ParticipantId);
      return `${socketUrl}?participantId=${gameIds.ParticipantId}&meetId=${gameIds.MeetId}`;
    } catch (ex) {
      logger.log(`FAILED TO GET GAME IDS FROM COOKIE:  ${document.cookie}`);
      setNotInvited(true);
    }
  }, []);

  const { sendJsonMessage, readyState } = useWebSocket(getSocketUrl, {
    reconnectAttempts: 10,
    reconnectInterval: 1000,
    onReconnectStop: () => {
      setMaxConnectionAttepmtsHit(true);
    },
    onError: (err) => {
      logger.log(`useSocket error: ${JSON.stringify(err)}`);
      setLoading(false);
    },
    onOpen: () => {
      sendJsonMessage({
        event: "join-meet",
        meetId: meetId,
      });
    },
    shouldReconnect: (closeEvent) => {
      return true;
    },
    onMessage: (event) => {
      const messageData = JSON.parse(event.data);
      if (messageData.finalLeaderboard) {
        setFinalLeaderboard(messageData.finalLeaderboard);
      }
      if (messageData.error) {
        logger.log(`SOCKET ERROR: ${messageData.error}`);
        setNotInvited(true);
      }
      if (messageData.placeBetsError) {
        setBets([]);
        showGlobalError(messageData.placeBetsError);
      }
      if (messageData.updatedParticipants) {
        setMeet(
          Object.assign(meet, {
            totalParticipants: messageData.totalParticipants,
            participants: messageData.updatedParticipants,
          })
        );
      }
      if (messageData.meet) {
        updateMeet(messageData.meet);
        if (messageData.meet.finished) {
          getFinalLeaderboard();
        }
      }
      if (messageData.bets) {
        const updatedBets = messageData.bets;
        setBalance(updatedBets.accountBalance);
        setPlacedBets(updatedBets.raceBets);
        setPosition(updatedBets.position);
        setBets([]);
        setValidationErrors([]);
      }
      if (messageData.refreshBets) {
        if (messageData.meet && messageData.meet.activeRace) {
          getBets(messageData.meet.activeRace.id);
        }
      }
      if (messageData.clearBets) {
        setPlacedBets([]);
        setBets([]);
      }
      setLoading(false);
      if (messageData.scrollToTop) {
        executeScrollToTop();
      }
    },
  });

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Connected",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState];

  useEffect(() => {
    const setGameIds = async () => {};
    setGameIds();
    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);

  async function updateMeet(meetData) {
    const activePlan = plans.find((p) => p.name === meetData.plan);
    if (activePlan) {
      setPlan(activePlan);
    }
    const currentActiveRace = meetData.activeRace;
    setActiveRace(currentActiveRace);
    setMeet(meetData);
    setIsOwner(meetData.ownerId === participantId);
    if (
      meetData.ownerId === participantId &&
      currentActiveRace &&
      currentActiveRace.video
    ) {
      setVideoUrl(currentActiveRace.video);
    }
  }

  function addToBettingSlip(e, dog) {
    e.preventDefault();
    setBets(
      bets.concat({
        number: dog.number,
        odds: dog.odds,
        oddsAsFraction: dog.oddsAsFraction,
        amount: undefined,
        eachWay: false,
        color: dog.color,
        colorUrl: dog.colorUrl,
      })
    );
    executeScroll();
  }

  function removeFromBettingSlip(e, index) {
    e.preventDefault();
    setValidationErrors([]);
    setBets(bets.filter((b, i) => i !== index));
  }

  function handleChange(e, betIndex) {
    const updatedBets = Array.from(bets);
    if (e.target.type === "checkbox") {
      updatedBets[betIndex][e.target.name] = e.target.checked;
    } else {
      if (e.target.name === "amount" && e.target.value[0] === ".") {
        e.target.value = `0${e.target.value}`;
      }
      updatedBets[betIndex][e.target.name] = e.target.value;
    }
    setBets(updatedBets);
  }

  async function placeBets() {
    if (!loading) {
      setLoading(true);
      const validationResult = betsSchema.validate(bets);
      if (validationResult.error) {
        setValidationErrors(validationResult.error.details);
      } else {
        const betsValue = bets.reduce((mem, b) => {
          const betValue = b.eachWay ? Number(b.amount * 2) : Number(b.amount);
          mem = mem + betValue;
          return mem;
        }, 0);
        if (betsValue > balance) {
          setValidationErrors([
            {
              path: ["balance"],
              message: "Value of bets is greater than your balance",
            },
          ]);
        } else {
          sendJsonMessage({
            event: "place-bets",
            meetId,
            raceId: activeRace.id,
            participantId,
            bets: bets.map((b) => {
              return {
                number: b.number,
                odds: b.odds,
                oddsAsFraction: b.oddsAsFraction,
                amount: b.amount,
                eachWay: b.eachWay,
              };
            }),
          });
        }
      }
      setLoading(false);
    }
  }

  function isFirstRaceNotStarted() {
    return (
      activeRace &&
      activeRace.id == 0 &&
      activeRace.status === RACE_STATUS.NEXT_RACE
    );
  }

  function sharingLink() {
    return `${httpUrl}/meets/${meet.entryToken}`;
  }

  function isLastRace() {
    return activeRace && meet.numRaces === activeRace.id + 1;
  }

  function toggleSpHelp(e) {
    e.preventDefault();
    setShowSpHelp(!showSpHelp);
  }

  const renderVideo = useMemo(() => {
    return activeRace ? (
      <div className="video-container">
        <CustomVideoPlayer
          numPlaces={activeRace.eachWayPays}
          racePlayed={racePlayed}
          videoTimes={activeRace.videoTimes}
          src={activeRace.video}
          horses={activeRace.dogs}
          overlayUrl={activeRace.overlayUrl}
        />
      </div>
    ) : null;
  }, [activeRace]);

  return (
    <Layout
      brand={meet && meet.brand}
      logoOveride={meet && meet.logo}
      backgroundColor={meet && meet.backgroundColor}
      homeUrl={meet && meet.homeUrl}
    >
      {notInvited ? (
        <NotInvited />
      ) : (
        <div>
          {connectionStatus !== "Connected" ? (
            <div className="connecting-overlay is-vcentered">
              {maxConnectionAttepmtsHit ? (
                <div className="failed-to-connect">
                  <h1 className="subtitle">
                    We&apos;re having difficulty getting you connected to the
                    game
                  </h1>
                  <p>
                    Are you on a VPN, or a work computer with some web blocking
                    software on it?
                  </p>
                  <p>
                    If so can you try disconnecting from your VPN and refreshing
                    the screen.
                  </p>
                  <p>
                    If you&apos;re still having trouble,{" "}
                    <a href={`${meet.homeUrl || siteUrl}/contact`}>
                      contact us
                    </a>
                  </p>
                </div>
              ) : (
                <div className="center">
                  {["Connecting", "Closed"].indexOf(connectionStatus) > -1 ? (
                    <i class="fas fa-circle-notch fa-spin"></i>
                  ) : null}
                  <br />
                  {connectionStatus}
                </div>
              )}
            </div>
          ) : null}
          {globalError ? (
            <div className="notification is-danger global-error is-light">
              {globalError}
            </div>
          ) : null}
          <div className="container" id="mainContainer">
            <div ref={topOfPage} className="section">
              <div>
                {!meet ? (
                  <div>Joining meet...</div>
                ) : (
                  <div>
                    {meet.started ? (
                      <div>
                        {meet.allRacesCompleted ? (
                          <FinalScores
                            meet={meet}
                            leaderboard={finalLeaderboard}
                            isOwner={isOwner}
                            homeUrl={meet && meet.homeUrl}
                          />
                        ) : (
                          <div className="columns">
                            <div className="column is-four-fifths">
                              <MeetTitle
                                meet={meet}
                                plan={plan}
                                isOwner={isOwner}
                              />
                              {isOwner ? (
                                <div className="pad-bottom">
                                  <a href="#" onClick={toggleSharingPanel}>
                                    Show the invite QR code
                                  </a>
                                </div>
                              ) : null}
                            </div>
                            <div className="column">
                              <div className="box">
                                <div className="has-text-centered">Balance</div>
                                <div className="balance has-text-centered">
                                  <Currency
                                    bettingCurrencySymbol={
                                      meet.bettingCurrencySymbol
                                    }
                                    amount={balance}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {isFirstRaceNotStarted() ? (
                          <div className="notification is-warning">
                            There are <strong>{meet.numRaces} races</strong> and
                            you have{" "}
                            <Currency
                              bettingCurrencySymbol={meet.bettingCurrencySymbol}
                              amount={balance}
                            />
                            . Spend it wisely!
                          </div>
                        ) : null}
                        {isLastRace() ? (
                          <div className="notification is-warning">
                            It&apos;s the last race.{" "}
                            <strong>Go big or go home.</strong>
                          </div>
                        ) : null}
                        {activeRace ? (
                          <div
                            key={`race-${activeRace.id}`}
                            className="race-panel"
                          >
                            <h2 className="title">
                              {activeRace.title}
                              <span style={{ float: "right" }}>
                                <small>{horseyDistance(activeRace.dist)}</small>
                              </span>
                            </h2>
                            <div className="each-way-div">
                              Each way pays top {activeRace.eachWayPays}
                            </div>
                            <div className="columns is-desktop">
                              {activeRace.status ===
                              RACE_STATUS.SHOW_RESULTS ? (
                                <div className="column">
                                  <div className="box">
                                    <h1 className="subtitle">Results</h1>
                                    <table className="table is-fullwidth flagged-table">
                                      <thead>
                                        <tr>
                                          <td className="has-text-centered">
                                            {raceTypeTitle()}
                                          </td>
                                          <td>SP</td>
                                          <td>Time</td>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {activeRace.results.map((result, i) => {
                                          return (
                                            <React.Fragment
                                              key={JSON.stringify(result)}
                                            >
                                              <tr
                                                className={
                                                  i < activeRace.eachWayPays
                                                    ? "is-selected joined-row-top"
                                                    : "joined-row-top"
                                                }
                                              >
                                                <td className="dog">
                                                  {meet.type === "horses" ? (
                                                    <HorseNumber
                                                      number={result.number}
                                                      colorUrl={result.colorUrl}
                                                    />
                                                  ) : (
                                                    <DogNumber
                                                      number={result.number}
                                                      color={result.color}
                                                    />
                                                  )}
                                                </td>
                                                <td>{result.oddsAsFraction}</td>
                                                <td>
                                                  {formatDuration(
                                                    result.finishTime
                                                  )}
                                                </td>
                                              </tr>
                                              <tr
                                                className={
                                                  i < activeRace.eachWayPays
                                                    ? "is-selected joined-row-bottom"
                                                    : "joined-row-bottom"
                                                }
                                              >
                                                <td
                                                  colSpan="3"
                                                  className="has-text-centered"
                                                >
                                                  <span>{result.name}</span>
                                                </td>
                                              </tr>
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  <BetsTable
                                    bettingCurrencySymbol={
                                      meet.bettingCurrencySymbol
                                    }
                                    horses={activeRace.dogs}
                                    bets={[]}
                                    placedBets={placedBets}
                                    meetType={meet.type}
                                  />
                                </div>
                              ) : (
                                <div className="column">
                                  <div className="box">
                                    <h1 className="subtitle">
                                      Place your bets
                                    </h1>
                                    <table className="table is-fullwidth flagged-table">
                                      <thead>
                                        <tr>
                                          <td></td>
                                          <td>{raceTypeTitle()}</td>
                                          <td>
                                            <a href="#" onClick={toggleSpHelp}>
                                              SP
                                            </a>
                                          </td>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {activeRace.dogs.map((dog) => {
                                          return (
                                            <React.Fragment
                                              key={`${activeRace.id}-${dog.number}`}
                                            >
                                              <tr className="joined-row-top">
                                                <td className="dog">
                                                  {meet.type === "horses" ? (
                                                    <HorseNumber
                                                      number={dog.number}
                                                      colorUrl={dog.colorUrl}
                                                    />
                                                  ) : (
                                                    <DogNumber
                                                      number={dog.number}
                                                      color={dog.color}
                                                    />
                                                  )}
                                                </td>
                                                <td className="dogName">
                                                  <span className="word-break">
                                                    {dog.name}
                                                  </span>
                                                  {dog.jockey ? (
                                                    <div
                                                      className="jockey"
                                                      title="jockey"
                                                    >
                                                      <small>
                                                        <i class="fas fa-hard-hat"></i>{" "}
                                                        {dog.jockey}
                                                      </small>
                                                    </div>
                                                  ) : null}
                                                </td>
                                                <td>{dog.oddsAsFraction}</td>
                                              </tr>
                                              <tr className="joined-row-bottom">
                                                <td colSpan="3">
                                                  {activeRace.status ===
                                                  RACE_STATUS.NOT_STARTED ? (
                                                    <span>
                                                      Betting not open yet
                                                    </span>
                                                  ) : null}
                                                  {activeRace.status ===
                                                  RACE_STATUS.NEXT_RACE ? (
                                                    <a
                                                      href="#"
                                                      className="button is-fullwidth is-light"
                                                      onClick={(e) =>
                                                        addToBettingSlip(e, dog)
                                                      }
                                                    >
                                                      Add to betting slip
                                                    </a>
                                                  ) : null}
                                                  {activeRace.status ===
                                                  RACE_STATUS.IN_PROGRESS ? (
                                                    <span>
                                                      Betting suspended
                                                    </span>
                                                  ) : null}
                                                  {activeRace.status ===
                                                  RACE_STATUS.COMPLETED ? (
                                                    <span>Betting closed</span>
                                                  ) : null}
                                                </td>
                                              </tr>
                                            </React.Fragment>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                  <div ref={betBox}>
                                    <ValidationSummary
                                      validationErrors={validationErrors}
                                    />
                                    <BetsTable
                                      bettingCurrencySymbol={
                                        meet.bettingCurrencySymbol
                                      }
                                      horses={activeRace.dogs}
                                      placedBets={placedBets}
                                      bets={bets}
                                      removeFromBettingSlip={
                                        removeFromBettingSlip
                                      }
                                      handleChange={handleChange}
                                      placeBets={placeBets}
                                      validationErrors={validationErrors}
                                      meetType={meet.type}
                                    />
                                  </div>
                                </div>
                              )}
                              <div className="column">
                                <Leaderboard
                                  bettingCurrencySymbol={
                                    meet.bettingCurrencySymbol
                                  }
                                  leaderBoard={meet.leaderBoard}
                                  totalParticipants={meet.totalParticipants}
                                />
                                <div className="box">
                                  <div className="has-text-centered">
                                    Your Leaderboard Position
                                  </div>
                                  <div className="balance has-text-centered">
                                    {position} / {meet.totalParticipants}
                                  </div>
                                </div>
                              </div>
                            </div>
                            {isOwner &&
                            activeRace.status === RACE_STATUS.NEXT_RACE ? (
                              <div className="section">
                                <GameButton
                                  submitted={submitted}
                                  setSubmitted={setSubmitted}
                                  loading={loading}
                                  setGlobalError={showGlobalError}
                                  action={startNextRace}
                                  text="Start Race"
                                />
                              </div>
                            ) : null}
                            {isOwner &&
                            activeRace.status === RACE_STATUS.SHOW_RESULTS ? (
                              <div className="section">
                                {!isLastRace() ? (
                                  <GameButton
                                    loading={loading}
                                    submitted={submitted}
                                    setSubmitted={setSubmitted}
                                    setGlobalError={showGlobalError}
                                    action={moveToNextRace}
                                    text="Next race"
                                  />
                                ) : (
                                  <GameButton
                                    isFullWidth={true}
                                    loading={loading}
                                    submitted={submitted}
                                    setSubmitted={setSubmitted}
                                    setGlobalError={showGlobalError}
                                    action={finishRaceNight}
                                    text="Show final scores!"
                                  />
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <GetStartedComponent
                        totalParticipants={meet.totalParticipants}
                        meet={meet}
                        submitted={submitted}
                        setSubmitted={setSubmitted}
                        setGlobalError={showGlobalError}
                        startMeet={startMeet}
                        participants={meet.participants}
                        isOwner={isOwner}
                        sharingLink={sharingLink}
                        plan={plan}
                      />
                    )}
                  </div>
                )}
                {videoUrl ? (
                  <div id="videoDiv" className="section">
                    <div className="notification is-info">
                      <h1 className="title">You&apos;ve got a job to do!</h1>
                      <p>
                        Ok champ, here&apos;s your moment... No one else can see
                        this video.
                      </p>
                      <p>
                        Make sure you <strong>share your screen</strong> before
                        you hit play on that thing, so everyone else can cheer
                        their {meet.type} on.
                      </p>
                    </div>
                    <div>
                      {activeRace.isYoutube ? (
                        <iframe
                          id="ytplayer"
                          type="text/html"
                          width="100%"
                          height={videoHeight}
                          src={videoUrl}
                          allowFullScreen
                          frameBorder="0"
                        ></iframe>
                      ) : (
                        renderVideo
                      )}
                    </div>
                    {showVideoWarning ? (
                      <div className="notification is-warning">
                        Make sure you play the video before moving to the next
                        race!
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {activeRace &&
                activeRace.status === RACE_STATUS.IN_PROGRESS &&
                isOwner ? (
                  <div className="section">
                    <GameButton
                      loading={loading}
                      submitted={submitted}
                      setSubmitted={setSubmitted}
                      setGlobalError={() => {}}
                      action={raceFinished}
                      text="Get Results"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          <div className={showSpHelp ? "modal is-active" : "modal"}>
            <div className="modal-background"></div>
            <div className="modal-card">
              <header className="modal-card-head">
                <p className="modal-card-title">SP: Starting Price</p>
              </header>
              <section className="modal-card-body">
                <p>
                  The <strong>starting price (SP)</strong> is the odds for the{" "}
                  {dogOrHorse()}.
                </p>
                <br />
                <p>
                  If a {dogOrHorse()} has an SP of 2/1, and you bet &pound;1 on
                  that {dogOrHorse()} to win, if the {dogOrHorse()} comes in
                  first place you will win &pound;2.00 back + your stake, so
                  &pound;3.00 in total.
                </p>
              </section>
              <footer className="modal-card-foot">
                <button onClick={toggleSpHelp} className="button">
                  Close
                </button>
              </footer>
            </div>
          </div>
          {meet && showSharingPanel ? (
            <div className={showSharingPanel ? "modal is-active" : "modal"}>
              <div className="modal-background"></div>
              <div className="modal-card">
                <header className="modal-card-head">
                  <h1 className="modal-card-title">
                    Share your screen to let more people join
                  </h1>
                </header>
                <section className="modal-card-body">
                  <div className="pad-bottom">
                    <QRCode
                      style={{ width: "400px", height: "400px" }}
                      value={sharingLink()}
                    />
                  </div>
                  <div>
                    <strong>
                      <CopyLink link={sharingLink()} />
                    </strong>
                  </div>
                </section>
                <footer className="modal-card-foot">
                  <button onClick={toggleSharingPanel} className="button">
                    Close
                  </button>
                </footer>
              </div>
            </div>
          ) : null}
          <style jsx>{`
            .pad-bottom {
              margin-bottom: 1em;
            }
            .dog {
              line-height: 1.5em;
            }
            h1 small {
              font-size: 0.575em;
            }
            .balance {
              font-size: 2em;
            }
            .flagged-table {
              line-height: 2.5em;
            }
            .joined-row-top td {
              border-bottom: 0;
            }
            .joined-row-bottom td {
              border-top: 0;
              padding-bottom: 1em;
            }
            .dogName {
              line-height: 1.5em !important;
            }
            .global-error {
              position: fixed;
              width: 100%;
              top: 0px;
              z-index: 100;
            }
            h2 small {
              font-size: 0.6em;
              color: #666;
            }
            .each-way-div {
              margin-bottom: 1em;
              font-weight: bold;
            }
            .connecting-overlay {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              min-height: 100%;
              background-color: rgba(0, 0, 0, 0.3);
              z-index: 9999;
              color: white;
            }
            .is-vcentered {
              display: flex;
              align-items: center;
            }
            .center {
              margin: auto;
              text-align: center;
            }
            .failed-to-connect {
              background-color: white;
              color: black;
              max-width: 700px;
              margin: auto;
              text-align: center;
              padding: 2em;
              border: 3px solid grey;
            }
            .jockey {
              color: #666;
            }
            .word-break: break-word;
          `}</style>
        </div>
      )}
    </Layout>
  );
}

export default Race;
